import { useEffect, useMemo } from 'react';
import useOpensStore from '@/stores/OpensStore';
import useUserStore from '@/stores/UserStore';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { cn } from '@/lib/utils';
import {
  getAllowedOpensConfig,
  getOpensConfig,
  getOpensStatus,
  saveOpensConfig,
} from '@/hooks/opens';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useOpensPopup } from '@/components/onboarding/OpensPopup';
import OpensConfig from '@/components/settings/OpensConfig';

export default function OpensSettings() {
  const navigate = useNavigate();

  const {
    enabled,
    hydrated,
    config,
    setEnabled,
    hydrateFromResponse,
    setAllowedConfigValues,
    updateConfig,
  } = useOpensStore(
    useShallow((s) => ({
      enabled: s.enabled,
      hydrated: s.hydrated,
      config: s.config,
      setEnabled: s.setEnabled,
      hydrateFromResponse: s.hydrateFromResponse,
      setAllowedConfigValues: s.setAllowedConfigValues,
      updateConfig: s.updateConfig,
    }))
  );

  const {
    opensSessionActive,
    setOpensSessionActive,
    opensFeatureEnabled,
    selfServiceOpensEnabled,
  } = useUserStore(
    useShallow((s) => ({
      opensSessionActive: s.opensSessionActive,
      setOpensSessionActive: s.setOpensSessionActive,
      opensFeatureEnabled: s.opensFeatureEnabled,
      selfServiceOpensEnabled: s.selfServiceOpensEnabled,
    }))
  );

  const isAuthenticated = useMemo(
    () => Boolean(opensSessionActive),
    [opensSessionActive]
  );

  const { openOpensPopup } = useOpensPopup();

  const checkOpensStatus = async () => {
    await toast.promise(getOpensStatus(), {
      loading: 'Checking status...',
      success: (res) => {
        if (
          res.message === 'Instagram session is active' &&
          res.data?.logged_in
        ) {
          setOpensSessionActive(true);
          return 'Instagram session is active !';
        } else if (res.message === 'Instagram session status retrieved') {
          return 'Instagram session is inactive !';
        }
      },
      error: (error) => {
        return (
          error.response?.data?.details?.error ||
          error.response?.data?.data?.details?.error ||
          'Failed to check status'
        );
      },
    });
  };

  const handleToggleEnabled = async (enabledValue) => {
    if (!config) return;

    const configData = {
      enabled: enabledValue,
      ...config,
    };

    await toast.promise(saveOpensConfig(configData), {
      loading: enabledValue ? 'Enabling opens...' : 'Disabling opens...',
      success: () => {
        // Separate enabled from config data before storing
        // eslint-disable-next-line no-unused-vars
        const { enabled, ...configWithoutEnabled } = configData;
        updateConfig(configWithoutEnabled);
        setEnabled(enabledValue);
        return enabledValue
          ? 'Opens automation enabled successfully!'
          : 'Opens automation disabled successfully!';
      },
      error: (error) => {
        return (
          error.response?.data?.message ||
          `Failed to ${enabledValue ? 'enable' : 'disable'} opens`
        );
      },
    });
  };

  useEffect(() => {
    if (opensFeatureEnabled === false) {
      navigate('/inbox');
    }
    if (selfServiceOpensEnabled === true) {
      navigate('/followers/self-service/config');
    }
  }, [opensFeatureEnabled, selfServiceOpensEnabled, navigate]);

  useEffect(() => {
    async function hydrate() {
      try {
        const res = await getOpensConfig();
        if (res && res.data) {
          hydrateFromResponse(res.data);
        }

        // Fetch allowed options
        const allowedConfigValues = await getAllowedOpensConfig();
        if (allowedConfigValues) {
          setAllowedConfigValues(
            allowedConfigValues.data.country_codes,
            allowedConfigValues.data.timezones
          );
        }
      } catch (err) {
        toast.error(
          err?.response?.data?.message || 'Failed to load opens settings'
        );
      }
    }

    if (!hydrated) {
      hydrate();
    }
  }, [hydrated, hydrateFromResponse, setAllowedConfigValues]);

  if (!opensFeatureEnabled) {
    return null;
  }

  return !hydrated ? (
    <div className='flex items-center justify-center py-24'>
      <Loader2 className='mr-2 h-5 w-5 animate-spin text-muted-foreground' />
      <span className='text-sm text-muted-foreground'>
        Loading opens settings...
      </span>
    </div>
  ) : (
    <div className='space-y-6 p-5'>
      <div className='flex items-center justify-between'>
        <div>
          {/* <h3 className='text-2xl font-bold'>Automated Opens</h3> */}
          <p className='text-sm text-muted-foreground'>
            <span className='inline-flex items-center gap-1 text-base font-medium'>
              <span>Session:</span>
              <span
                className={
                  opensSessionActive
                    ? 'inline-flex items-center rounded-full px-0.5 py-0.5 text-green-800'
                    : 'inline-flex items-center rounded-full px-0.5 py-0.5 text-red-800'
                }
              >
                {opensSessionActive ? 'Active' : 'Inactive'}
              </span>
            </span>
          </p>
        </div>
        <div className='flex items-center gap-3'>
          {config && opensSessionActive && (
            <div className='flex items-center gap-1 rounded-lg border bg-muted p-1'>
              <button
                onClick={() => handleToggleEnabled(true)}
                className={cn(
                  'rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200',
                  enabled
                    ? 'bg-green-600 text-white'
                    : 'text-muted-foreground hover:bg-muted/50'
                )}
              >
                Enabled
              </button>
              <button
                onClick={() => handleToggleEnabled(false)}
                className={cn(
                  'rounded-md px-3 py-1 text-sm font-medium transition-colors duration-200',
                  enabled === false
                    ? 'bg-background text-muted-foreground'
                    : 'text-muted-foreground hover:bg-muted/50'
                )}
              >
                Disabled
              </button>
            </div>
          )}
          {config && !opensSessionActive && (
            <Button size='xs' variant='outline' onClick={checkOpensStatus}>
              Check Status
            </Button>
          )}
          {config && (
            <Button size='sm' onClick={openOpensPopup}>
              {isAuthenticated ? 'Re-authenticate' : 'Authenticate'}
            </Button>
          )}
        </div>
      </div>

      <Separator />

      <OpensConfig />
    </div>
  );
}
